import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import parse from 'html-react-parser';
import { SectionBox } from '@/components';
import { useAppSelector } from '@/redux/hooks';
import { profileSelectors } from '@/redux/selectors';
import { BonusFlowValueEnum, BonusItem, BonusTypeEnum } from '@/api';
import { getFormattedDate } from '@/helpers';

import styles from './YourBonus.module.sass';

const bonusStrParams = (bonus: BonusItem, defaultMode = '') => {
  const count = Number(bonus.value);
  const mode =
    bonus.flow_value === BonusFlowValueEnum.FLOW_ANY ? defaultMode : bonus.flow;
  const dateTime = getFormattedDate(bonus.valid_until);

  return { count, mode, dateTime };
};

interface YourBonusProps {}

const YourBonus: FC<YourBonusProps> = () => {
  const { t } = useTranslation();
  const { data: profileData } = useAppSelector(
    profileSelectors.getProfileSlice,
  );
  const hasBonuses =
    !!profileData?.bonuses &&
    profileData.bonuses.length > 0 &&
    profileData.bonuses.find(
      (bonus) => bonus.type !== BonusTypeEnum.TYPE_MONITORING,
    );
  const defaultMode = t('translation.bonus_flow_any');

  if (!hasBonuses) {
    return null;
  }

  const sectionTitle = t('translation.user_bonuses_title');
  const investigationBonusGroups = profileData?.bonuses
    .filter((bonus) => bonus.type === BonusTypeEnum.TYPE_INVESTIGATION)
    .reduce((validUntils: Record<string, number>, item) => {
      const untilDate = item.valid_until;
      validUntils[untilDate] =
        (validUntils[untilDate] ?? 0) + Number(item.value);

      return validUntils;
    }, {});

  const generalBonuses = profileData?.bonuses.filter(
    (bonus) =>
      [
        BonusTypeEnum.TYPE_MONITORING,
        BonusTypeEnum.TYPE_INVESTIGATION,
      ].includes(bonus.type) === false,
  );

  return (
    <SectionBox>
      {{
        // TODO large title
        title: sectionTitle,
        body: (
          <div className={styles.list}>
            {!!Object.keys(investigationBonusGroups).length && (
              <>
                {Object.keys(investigationBonusGroups).map(
                  (validUntil: string) => (
                    <div key={validUntil} className={styles.bonus}>
                      {parse(
                        t('translation.bonus_investigation_description', {
                          count: Number(investigationBonusGroups[validUntil]),
                          date: getFormattedDate(validUntil),
                        }),
                      )}
                    </div>
                  ),
                )}
              </>
            )}

            {generalBonuses.map((bonus: BonusItem) => (
              <div key={bonus.id} className={styles.bonus}>
                {parse(
                  t(
                    'translation.bonus_description_str',
                    bonusStrParams(bonus, defaultMode),
                  ),
                )}
              </div>
            ))}
          </div>
        ),
      }}
    </SectionBox>
  );
};

export default YourBonus;
